import React, { useCallback, useContext, useEffect, useState } from 'react';
import { HighlightOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { ErrorFinder } from '../../../models';
import { UserContext } from '../../contexts';
import { ErrorFinderEditPage } from './error-finder-edit-page';
import { EntityChangeLog } from '../../reports';

export function ErrorFindersPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [errorFinders, setErrorFinders] = useState<ErrorFinder[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalErrorFinder, seteditModalErrorFinder] = useState<ErrorFinder>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'errorFinders.edit'));
  }, [user]);

  const loadErrorFinders = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`error-finders`)
      .then((response: any) => {
        setErrorFinders(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadErrorFinders();
  }, [loadErrorFinders]);

  const onRefreshClick = () => {
    loadErrorFinders();
  };

  const onErrorFinderEditSaved = () => {
    closeEditModal();
    loadErrorFinders();
  };

  const openEditModal = () => {
    seteditModalErrorFinder(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByErrorFinder = (errorFinder: ErrorFinder) => {
    seteditModalErrorFinder(errorFinder);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    seteditModalErrorFinder(undefined);
  };

  const deleteErrorFinder = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`error-finders/${id}`)
      .then(() => {
        setLoading(false);
        loadErrorFinders();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteErrorFinder = (id: string) => {
    const onOk = () => deleteErrorFinder(id);
    Modal.confirm({
      title: 'Hata tespit eden sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Hata tespit eden silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const errorFinder = errorFinders?.find(m => m.id === id);
      if (errorFinder) {
        openEditModalByErrorFinder(errorFinder);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteErrorFinder(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, errorFinder: ErrorFinder) => (
        <Button type="link" onClick={() => openEditModalByErrorFinder(errorFinder)}>
          {name}
        </Button>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, errorFinder: ErrorFinder) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(errorFinder.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <HighlightOutlined /> Hata tespit edenler
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni hata tespit eden
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={errorFinders} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'ErrorFinder'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Hata tespit eden" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <ErrorFinderEditPage errorfinder={editModalErrorFinder} onCancel={closeEditModal} onSaved={onErrorFinderEditSaved} />
      </Modal>
    </>
  );
}
